import { IconArrowLeft, IconArrowRight, IconX } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { WizardProgress } from "@/components/patterns/WizardProgress";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogPopup,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { PolicyStep } from "./create-managed-group/PolicyStep";
import { ReviewStep } from "./create-managed-group/ReviewStep";
import { ScopeStep } from "./create-managed-group/ScopeStep";
import { useCreateManagedGroupWizard } from "./create-managed-group/use-create-managed-group-wizard";

export {
  canReuseManagedGroupDiagnosticsPlaceholder,
  managedGroupDiagnosticsQueryKey,
} from "./create-managed-group/managed-group-diagnostics-query";

interface CreateManagedGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateManagedGroupDialog({
  open,
  onOpenChange,
}: CreateManagedGroupDialogProps): React.ReactElement {
  const { t } = useTranslation("managedGroups");
  const {
    step,
    setStep,
    shopId,
    name,
    setName,
    selectedCampaigns,
    campaignPage,
    setCampaignPage,
    campaignSearch,
    campaignDeliveryStatus,
    campaignPortfolioId,
    campaignAiStatus,
    campaignSortBy,
    campaignSortDirection,
    selectedCampaignPage,
    setSelectedCampaignPage,
    policy,
    setPolicy,
    campaignsQuery,
    portfoliosQuery,
    diagnosticsQuery,
    mutation,
    reset,
    changeShop,
    addCampaigns,
    removeCampaigns,
    changeCampaignSearch,
    changeCampaignDeliveryStatus,
    changeCampaignPortfolioId,
    changeCampaignAiStatus,
    changeCampaignSort,
    selectedCount,
    alreadyAiOnCount,
    canContinueScope,
    canContinuePolicy,
    workspaceAiEnabled,
    selectedShop,
    shopItems,
    campaigns,
    campaignTotal,
    campaignPageSize,
  } = useCreateManagedGroupWizard({ open, onOpenChange });
  const dialogSubtitle =
    step === 1
      ? t("create.scopeDescription")
      : step === 2
        ? t("create.nameAndCampaigns", { name: name.trim(), count: selectedCount })
        : t("create.reviewDescription");

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && mutation.isPending) return;
        if (!nextOpen) reset();
        onOpenChange(nextOpen);
      }}
    >
      <DialogPopup
        showCloseButton={false}
        bottomStickOnMobile={false}
        backdropClassName="bg-ink/35 backdrop-blur-none"
        viewportClassName="grid-rows-[1fr_auto_1fr] p-4"
        className={cn(
          "relative max-h-[calc(100vh-32px)] max-w-none overflow-hidden rounded-[14px] border border-line bg-surface text-ink shadow-[var(--paper-overlay-shadow)] transition-[width,height,scale,opacity] duration-200 data-ending-style:scale-[0.97] data-ending-style:opacity-0 data-starting-style:scale-[0.97] data-starting-style:opacity-0",
          step === 1 && "h-[780px] w-[930px]",
          step === 2 && "h-[800px] w-[860px]",
          step === 3 && "h-[690px] w-[760px]",
        )}
      >
        <header className="flex h-[76px] shrink-0 items-center justify-between border-b border-line px-[22px]">
          <div className="flex flex-col gap-[3px]">
            <DialogTitle className="text-base leading-[22px] font-semibold">
              {t("create.title")}
            </DialogTitle>
            <DialogDescription className="text-[11px] leading-4 text-muted-foreground">
              {dialogSubtitle}
            </DialogDescription>
          </div>
          <DialogClose
            render={<Button size="icon-sm" variant="outline" />}
            className="size-7 rounded-[8px] [&_svg]:m-0"
            aria-label={t("actions.close")}
          >
            <IconX className="size-[15px]" />
          </DialogClose>
        </header>

        <WizardStepper step={step} />

        <main className="min-h-0 flex-1">
          {step === 1 ? (
            <ScopeStep
              key={shopId}
              campaignPage={campaignPage}
              campaignPageSize={campaignPageSize}
              campaignSearch={campaignSearch}
              campaignDeliveryStatus={campaignDeliveryStatus}
              campaignPortfolioId={campaignPortfolioId}
              campaignAiStatus={campaignAiStatus}
              campaignSortBy={campaignSortBy}
              campaignSortDirection={campaignSortDirection}
              campaignTotal={campaignTotal}
              campaigns={campaigns}
              portfolioOptions={portfoliosQuery.data ?? []}
              isCampaignError={campaignsQuery.isError}
              isPortfolioError={portfoliosQuery.isError}
              isLoading={
                campaignsQuery.isLoading ||
                campaignsQuery.isFetching ||
                portfoliosQuery.isLoading ||
                portfoliosQuery.isFetching
              }
              name={name}
              selectedCampaignPage={selectedCampaignPage}
              selectedCampaigns={selectedCampaigns}
              shopId={shopId}
              shopItems={shopItems}
              onCampaignPageChange={setCampaignPage}
              onRetryCampaigns={() => void campaignsQuery.refetch()}
              onRetryPortfolios={() => void portfoliosQuery.refetch()}
              onCampaignSearchChange={changeCampaignSearch}
              onCampaignDeliveryStatusChange={changeCampaignDeliveryStatus}
              onCampaignPortfolioIdChange={changeCampaignPortfolioId}
              onCampaignAiStatusChange={changeCampaignAiStatus}
              onCampaignSortChange={changeCampaignSort}
              onAddCampaigns={addCampaigns}
              onNameChange={setName}
              onRemoveCampaigns={removeCampaigns}
              onSelectedCampaignPageChange={setSelectedCampaignPage}
              onShopChange={changeShop}
            />
          ) : step === 2 ? (
            <PolicyStep
              alreadyAiOnCount={alreadyAiOnCount}
              diagnostics={diagnosticsQuery.data}
              diagnosticsError={diagnosticsQuery.isError}
              diagnosticsLoading={diagnosticsQuery.isLoading}
              policy={policy}
              selectedCount={selectedCount}
              onRetryDiagnostics={() => void diagnosticsQuery.refetch()}
              onChange={setPolicy}
            />
          ) : (
            <ReviewStep
              name={name.trim()}
              diagnostics={diagnosticsQuery.data}
              diagnosticsError={diagnosticsQuery.isError}
              diagnosticsLoading={diagnosticsQuery.isLoading}
              policy={policy}
              selectedCount={selectedCount}
              shopName={selectedShop?.shopName ?? "—"}
              workspaceAiEnabled={workspaceAiEnabled}
              onRetryDiagnostics={() => void diagnosticsQuery.refetch()}
            />
          )}
        </main>

        <footer className="flex h-[72px] shrink-0 items-center justify-between border-t border-line px-[22px]">
          {step === 1 ? (
            <p className="text-[10px] leading-[15px] text-muted-foreground">
              {t("create.selectionLimit")}
            </p>
          ) : step === 2 ? (
            <p className="text-[10px] leading-3 text-muted-foreground">
              {t("create.nextRoundNotice")}
            </p>
          ) : null}

          {step === 1 ? (
            <div className="flex items-center gap-2">
              <DialogClose render={<Button variant="outline" />} disabled={mutation.isPending}>
                {t("actions.cancel")}
              </DialogClose>
              <Button disabled={!canContinueScope} onClick={() => setStep(2)}>
                {t("actions.continue")}
                <IconArrowRight data-icon="inline-end" />
              </Button>
            </div>
          ) : step === 2 ? (
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setStep(1)}>
                <IconArrowLeft data-icon="inline-start" />
                {t("actions.back")}
              </Button>
              <Button
                data-slot="policy-continue-action"
                disabled={!canContinuePolicy}
                onClick={() => setStep(3)}
              >
                {t("actions.continue")}
                <IconArrowRight data-icon="inline-end" />
              </Button>
            </div>
          ) : (
            <div data-slot="confirmation-actions" className="ml-auto flex items-center gap-2">
              <Button variant="outline" disabled={mutation.isPending} onClick={() => setStep(2)}>
                <IconArrowLeft data-icon="inline-start" />
                {t("actions.back")}
              </Button>
              <Button
                loading={mutation.isPending}
                onClick={() => mutation.mutate(workspaceAiEnabled ? "active" : "paused")}
              >
                {t("actions.save")}
              </Button>
            </div>
          )}
        </footer>
      </DialogPopup>
    </Dialog>
  );
}

function WizardStepper({ step }: { step: 1 | 2 | 3 }): React.ReactElement {
  const { t } = useTranslation("managedGroups");
  return (
    <WizardProgress
      currentStep={step}
      labels={[t("create.steps.scope"), t("create.steps.policy"), t("create.steps.review")]}
    />
  );
}
